<template>
  <div>
    <notifications />
    <navbar :docsName="docsName" />

    <ValidationObserver v-slot="{ invalid }">
      <div class="container-fluid row">
        <div class="col-xl-12">
          <div class="row">
            <div class="col-xl-12 block br-t-l-0 mb-3">
              <head-menu
                :col="12"
                class="d-xl-flex"
                :headline="isNew ? 'SSO Tenant erstellen' : 'SSO Tenant bearbeiten'"
              />
              <div class="row mt-2 d-flex justify-content-between">
                <div class="ml-4">
                  <button
                    v-shortkey.once="['ctrl', 's']"
                    class="btn btn-success mr-2"
                    :disabled="invalid || saving"
                    @click="speichern"
                    @shortkey="speichern"
                  >
                    <span v-if="saving" class="spinner-border spinner-border-sm mr-2" />
                    {{ $t('global.save') }}
                  </button>
                  <button class="btn btn-primary mr-2" @click="$router.push({ name: 'sso-tenant-liste' })">
                    <font-awesome-icon icon="fa-solid fa-list" class="mr-2" />
                    {{ $t('global.tolist') }}
                  </button>
                </div>
                <div class="mr-4">
                  <button
                    v-if="!isNew"
                    class="btn btn-danger"
                    @click="$bvModal.show('sso-tenant-loeschen-modal')"
                  >
                    <font-awesome-icon icon="fa-duotone fa-trash" class="mr-1" />
                    {{ $t('global.delete') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-6 pr-4">
          <container-headline headline="SSO Konfiguration" :col="4" />
          <div class="row mb-3">
            <div class="col-xl-12 block br-t-l-0">
              <div class="eingabe-panel-kopf p-3">

                <div class="row">
                  <div class="col-xl-12">
                    <div class="form-group">
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <input
                          v-model.trim="form.name"
                          class="form-control"
                          placeholder=" "
                        />
                        <label :class="{ 'text-danger': errors.length > 0 }">Name *</label>
                      </ValidationProvider>
                      <small class="text-muted">Anzeigename im Login-Button, z.B. <code>Microsoft</code> oder <code>Zoho</code></small>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-xl-12">
                    <div class="form-group">
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <input
                          v-model.trim="form.serverDomain"
                          class="form-control"
                          placeholder=" "
                        />
                        <label :class="{ 'text-danger': errors.length > 0 }">Server-Domain (Hostname) *</label>
                      </ValidationProvider>
                      <small class="text-muted">z.B. <code>clientcorp.bx-education.com</code></small>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-xl-12">
                    <div class="form-group">
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <input
                          v-model.trim="form.issuerUrl"
                          class="form-control"
                          placeholder=" "
                        />
                        <label :class="{ 'text-danger': errors.length > 0 }">Issuer URL *</label>
                      </ValidationProvider>
                      <small class="text-muted">
                        Entra ID: <code>https://login.microsoftonline.com/&lt;tenant-id&gt;/v2.0</code><br />
                        Google: <code>https://accounts.google.com</code>
                      </small>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-xl-12">
                    <div class="form-group">
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <input
                          v-model.trim="form.clientId"
                          class="form-control"
                          placeholder=" "
                        />
                        <label :class="{ 'text-danger': errors.length > 0 }">Client ID *</label>
                      </ValidationProvider>
                    </div>
                  </div>
                </div>

                <!-- Client Secret — write-only after first save -->
                <div class="row">
                  <div class="col-xl-12">
                    <div class="form-group">
                      <ValidationProvider :rules="isNew ? 'required' : ''" v-slot="{ errors }">
                        <div class="input-group">
                          <input
                            v-if="editingSecret || isNew"
                            v-model="form.clientSecret"
                            type="password"
                            class="form-control"
                            placeholder="Neues Secret eingeben"
                            autocomplete="new-password"
                          />
                          <input
                            v-else
                            type="password"
                            class="form-control"
                            :value="secretPlaceholder"
                            readonly
                          />
                          <div class="input-group-append">
                            <button
                              v-if="!isNew"
                              type="button"
                              class="btn btn-outline-secondary"
                              @click="toggleSecretEdit"
                            >
                              {{ editingSecret ? 'Abbrechen' : 'Ändern' }}
                            </button>
                          </div>
                        </div>
                        <label :class="{ 'text-danger': isNew && errors.length > 0 }">
                          Client Secret{{ isNew ? ' *' : '' }}
                        </label>
                      </ValidationProvider>
                      <small v-if="!isNew && !editingSecret" class="text-muted">
                        <font-awesome-icon icon="fa-regular fa-circle-check" class="text-success mr-1" />
                        Secret ist gespeichert. Klicken Sie "Ändern" um es zu ersetzen.
                      </small>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-6 pr-4">
          <container-headline headline="Einstellungen" :col="4" />
          <div class="row mb-3">
            <div class="col-xl-12 block br-t-l-0">
              <div class="eingabe-panel-kopf p-3">

                <div v-if="!isNew" class="row">
                  <div class="col-xl-12">
                    <div class="form-group">
                      <div class="custom-control custom-switch mt-2">
                        <input
                          id="activ-switch"
                          v-model="form.activ"
                          type="checkbox"
                          class="custom-control-input"
                        />
                        <label class="custom-control-label" for="activ-switch">
                          {{ $t('global.active') }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </ValidationObserver>

    <loading-overlay v-show="loading" />

    <b-modal
      id="sso-tenant-loeschen-modal"
      title="SSO Tenant löschen"
      ok-variant="danger"
      :ok-title="$t('global.delete')"
      :cancel-title="$t('global.cancel')"
      @ok="deleteTenant"
    >
      Möchten Sie den SSO Tenant für <strong>{{ form.serverDomain }}</strong> wirklich löschen?
    </b-modal>
  </div>
</template>

<script>
import Api from "@/api/AppApi";
import page from "@/mixins/Page";
import Navbar from "@/components/Navbar";
import HeadMenu from "@/components/HeadMenu";
import ContainerHeadline from "@/components/ContainerHeadline";
import store from "@/store";

export default {
  name: "SsoTenant",
  components: { Navbar, HeadMenu, ContainerHeadline },
  mixins: [page],
  store,
  props: {
    id: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      saving: false,
      editingSecret: false,
      docsName: "SsoTenant",
      form: {
        name:         "",
        serverDomain: "",
        issuerUrl:    "",
        clientId:     "",
        clientSecret: "",
        activ:        true,
      },
    };
  },
  computed: {
    isNew() {
      return !this.id;
    },
    secretPlaceholder() {
      return "••••••••••••••••";
    },
  },
  async mounted() {
    if (!this.isNew) {
      await this.loadTenant();
    }
  },
  methods: {
    async loadTenant() {
      this.loading = true;
      try {
        const res = await Api.get(`stammdaten/sso-tenant?id=${this.id}`);
        const d   = res.data;
        this.form.name         = d.name         ?? "";
        this.form.serverDomain = d.serverDomain ?? "";
        this.form.issuerUrl    = d.issuerUrl    ?? "";
        this.form.clientId    = d.clientId     ?? "";
        this.form.activ       = d.activ        ?? true;
        // clientSecret is never returned by the API — form.clientSecret stays empty
      } catch (e) {
        this.$notify({ type: "error", title: this.$t('global.error'), text: e.message });
      } finally {
        this.loading = false;
      }
    },
    toggleSecretEdit() {
      this.editingSecret = !this.editingSecret;
      if (!this.editingSecret) {
        this.form.clientSecret = "";
      }
    },
    async speichern() {
      this.saving = true;
      try {
        const payload = {
          name:         this.form.name,
          serverDomain: this.form.serverDomain,
          issuerUrl:    this.form.issuerUrl,
          clientId:     this.form.clientId,
          activ:        this.form.activ,
        };
        // Only include the secret when the user has explicitly entered a new one
        if (this.form.clientSecret) {
          payload.clientSecret = this.form.clientSecret;
        }

        if (this.isNew) {
          const res = await Api.post("stammdaten/sso-tenant", payload);
          if (res.data && res.data.error) {
            this.$notify({ type: "error", title: this.$t('global.error'), text: res.data.error });
            return;
          }
          this.$notify({ type: "success", title: this.$t('global.actionsuccessful') });
          this.$router.replace({ name: "sso-tenant", params: { id: res.data.id } });
        } else {
          const res = await Api.put(`stammdaten/sso-tenant?id=${this.id}`, payload);
          if (res.data && res.data.error) {
            this.$notify({ type: "error", title: this.$t('global.error'), text: res.data.error });
            return;
          }
          this.$notify({ type: "success", title: this.$t('global.actionsuccessful') });
          this.editingSecret    = false;
          this.form.clientSecret = "";
        }
      } catch (e) {
        this.$notify({ type: "error", title: this.$t('global.error'), text: e.message });
      } finally {
        this.saving = false;
      }
    },
    async deleteTenant() {
      try {
        await Api.delete(`stammdaten/sso-tenant?id=${this.id}`);
        this.$notify({ type: "success", title: this.$t('global.deleted') });
        this.$router.push({ name: "sso-tenant-liste" });
      } catch (e) {
        this.$notify({ type: "error", title: this.$t('global.error'), text: e.message });
      }
    },
  },
};
</script>
